// Rich-text editor for RICHTEXT transaction columns.
//
// The column stores HTML — the engine sanitises it on every write
// (`twasta_runtime_engine/rich_html.py`) and every read surface sanitises it
// again (`richContent.ts`), so this editor's schema is deliberately the same
// allow-list: headings h2–h4 (h1 belongs to the page title), bold / italic /
// underline / strike, lists, quote, code block, links, images, tables and a
// divider. Anything the editor cannot represent never reaches the column.
//
// Images are UPLOADED (never pasted in as base64) through the host's
// `uploadImage` transport, which stores them where a published page can serve
// them. A host without one still lets the author place an image by URL.

import { useEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  Quote, Code2, Link2, Image as ImageIcon, Table as TableIcon, Minus, Undo2,
  Redo2, Loader2, X, Trash2,
} from 'lucide-react'
import { richPlainText, sanitizeRichHtml } from '../../richContent'
import { useTxnRuntime } from '../context'

export interface RichTextUploadResult { url: string }

interface Props {
  value: string
  onChange: (html: string) => void
  disabled?: boolean
  placeholder?: string
  /** Upload an image placed in the body; resolves to its public URL. */
  uploadImage?: (file: File) => Promise<RichTextUploadResult>
  onError?: (message: string) => void
  minHeight?: number
  maxHeight?: number
  hasError?: boolean
  fieldKey?: string
  autoFocus?: boolean
}

// Image with its intrinsic size kept on the node: a published page needs
// width/height on every <img> or the layout jumps while it loads (CLS).
const SizedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null },
      height: { default: null },
    }
  },
}).configure({ inline: false, allowBase64: false })

const ACCEPTED_IMAGES = 'image/png,image/jpeg,image/gif,image/webp,image/avif'

// An empty document serialises as `<p></p>`; the column should read as empty.
function normaliseOut(html: string): string {
  const t = html.trim()
  return t === '<p></p>' || t === '' ? '' : t
}

async function imageSize(file: File): Promise<{ width?: number; height?: number }> {
  try {
    if (typeof createImageBitmap !== 'function') return {}
    const bmp = await createImageBitmap(file)
    const out = { width: bmp.width, height: bmp.height }
    bmp.close?.()
    return out
  } catch {
    return {}
  }
}

export default function RichTextField({
  value, onChange, disabled, placeholder, uploadImage, onError,
  minHeight = 180, maxHeight = 560, hasError, fieldKey, autoFocus,
}: Props) {
  const lastEmitted = useRef<string>(value || '')
  const [uploading, setUploading] = useState(0)
  const [panel, setPanel] = useState<null | 'link' | 'image'>(null)
  const report = (msg: string) => { if (onError) onError(msg); else console.error(`[RichTextField] ${msg}`) }

  // Upload + insert; used by the toolbar picker, paste and drop.
  const insertFiles = async (editor: Editor, files: File[], pos?: number) => {
    const images = files.filter(f => ACCEPTED_IMAGES.split(',').includes(f.type))
    if (!images.length) return false
    if (!uploadImage) {
      report('Image upload is not available here — insert the image by URL instead.')
      return true
    }
    for (const file of images) {
      setUploading(n => n + 1)
      try {
        const [{ url }, size] = await Promise.all([uploadImage(file), imageSize(file)])
        const alt = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim()
        const node = { type: 'image', attrs: { src: url, alt, ...size } }
        if (pos != null) editor.chain().focus().insertContentAt(pos, node).run()
        else editor.chain().focus().insertContent(node).run()
      } catch (e) {
        report(`Could not upload ${file.name}: ${e instanceof Error ? e.message : String(e)}`)
      } finally {
        setUploading(n => n - 1)
      }
    }
    return true
  }
  const insertFilesRef = useRef(insertFiles)
  insertFilesRef.current = insertFiles
  // Paste/drop handlers are registered once with the editor; they read the
  // live editor through this ref.
  const editorRef = useRef<Editor | null>(null)

  const extensions = useMemo(() => [
    // tiptap 3's StarterKit bundles Link + Underline (registered below with
    // our own options) and TrailingNode (would append a paragraph to every
    // stored body) — off, so the schema is what it was.
    StarterKit.configure({
      heading: { levels: [2, 3, 4] },
      link: false, underline: false, trailingNode: false,
    }),
    Underline,
    Link.configure({
      openOnClick: false, autolink: true, linkOnPaste: true,
      protocols: ['http', 'https', 'mailto', 'tel'],
      HTMLAttributes: { rel: 'noopener noreferrer', target: null },
    }),
    SizedImage,
    Placeholder.configure({ placeholder: placeholder || 'Write here…' }),
    Table.configure({ resizable: false }),
    TableRow, TableHeader, TableCell,
  ], [placeholder])

  const editor = useEditor({
    // tiptap 3 stopped re-rendering on every transaction; toolbars and
    // node views here read editor state during render, so keep v2's behaviour.
    shouldRerenderOnTransaction: true,
    extensions,
    content: sanitizeRichHtml(value || ''),
    editable: !disabled,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor: ed }) => {
      const html = normaliseOut(ed.getHTML())
      lastEmitted.current = html
      onChange(html)
    },
    editorProps: {
      attributes: {
        class: 'tw-richtext tw-richtext-editor',
        'data-livepreview-field': fieldKey || '',
      },
      handlePaste: (view, event) => {
        const files = Array.from(event.clipboardData?.files || [])
        if (!files.length || !editorRef.current) return false
        event.preventDefault()
        void insertFilesRef.current(editorRef.current, files)
        return true
      },
      handleDrop: (view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files || [])
        if (!files.length || !editorRef.current) return false
        event.preventDefault()
        const at = view.posAtCoords({ left: (event as DragEvent).clientX, top: (event as DragEvent).clientY })
        void insertFilesRef.current(editorRef.current, files, at?.pos)
        return true
      },
    },
  }, [extensions])
  editorRef.current = editor

  // A new record (or a value set by item_change) replaces the document; our
  // own keystrokes echo back through `value` and must not reset the cursor.
  useEffect(() => {
    if (!editor) return
    const incoming = value || ''
    if (incoming === lastEmitted.current) return
    lastEmitted.current = incoming
    editor.commands.setContent(sanitizeRichHtml(incoming), { emitUpdate: false })
  }, [value, editor])

  useEffect(() => { editor?.setEditable(!disabled) }, [disabled, editor])

  if (!editor) return null
  const inTable = editor.isActive('table')
  const imageSelected = editor.isActive('image')

  return (
    <div
      className="tw-richtext-field"
      style={{
        border: `1px solid ${hasError ? 'rgba(239,68,68,0.6)' : 'var(--color-border)'}`,
        borderRadius: 6,
        background: disabled ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
        display: 'flex', flexDirection: 'column', minWidth: 0,
      }}
    >
      <style>{RICHTEXT_EDITOR_CSS}</style>
      {!disabled && (
        <Toolbar
          editor={editor}
          panel={panel}
          setPanel={setPanel}
          uploading={uploading > 0}
          canUpload={!!uploadImage}
          onPickFiles={files => void insertFiles(editor, files)}
        />
      )}
      {!disabled && panel === 'link' && (
        <LinkPanel editor={editor} onClose={() => setPanel(null)} />
      )}
      {!disabled && panel === 'image' && (
        <ImageUrlPanel editor={editor} onClose={() => setPanel(null)} />
      )}
      {!disabled && imageSelected && <ImageAltBar editor={editor} />}
      {!disabled && inTable && <TableBar editor={editor} />}
      <div style={{ minHeight, maxHeight, overflowY: 'auto', padding: '10px 14px' }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

// ── Toolbar ──────────────────────────────────────────────────────────────

function Btn({ on, title, onClick, children, disabled }: {
  on?: boolean; title: string; onClick: () => void; children: React.ReactNode; disabled?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={on}
      disabled={disabled}
      onMouseDown={e => e.preventDefault()}   // keep the editor's selection
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 4, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: on ? 'var(--color-accent-weak, rgba(76,141,255,0.16))' : 'transparent',
        color: on ? 'var(--color-accent)' : 'var(--color-text)',
        opacity: disabled ? 0.4 : 1,
      }}
    >{children}</button>
  )
}

const Sep = () => <span style={{ width: 1, alignSelf: 'stretch', margin: '4px 4px', background: 'var(--color-border)' }} />

function Toolbar({ editor, panel, setPanel, uploading, canUpload, onPickFiles }: {
  editor: Editor
  panel: null | 'link' | 'image'
  setPanel: (p: null | 'link' | 'image') => void
  uploading: boolean
  canUpload: boolean
  onPickFiles: (files: File[]) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const block = editor.isActive('heading', { level: 2 }) ? 'h2'
    : editor.isActive('heading', { level: 3 }) ? 'h3'
      : editor.isActive('heading', { level: 4 }) ? 'h4' : 'p'
  const setBlock = (v: string) => {
    const c = editor.chain().focus()
    if (v === 'p') c.setParagraph().run()
    else c.toggleHeading({ level: Number(v[1]) as 2 | 3 | 4 }).run()
  }
  const size = 15
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
      padding: '4px 6px', borderBottom: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)', borderRadius: '6px 6px 0 0',
      position: 'sticky', top: 0, zIndex: 1,
    }}>
      <select
        value={block}
        onChange={e => setBlock(e.target.value)}
        aria-label="Text style"
        // The host's global form CSS gives every <select> width:100% and
        // roomy padding — in this toolbar that pushes the buttons onto a
        // second row and clips the label, so size it to its content here.
        style={{
          flex: '0 0 auto', width: 'auto', minWidth: 0, maxWidth: '100%',
          height: 28, padding: '0 26px 0 8px', lineHeight: '26px', margin: '0 4px 0 0',
          fontSize: '0.8625rem', borderRadius: 4,
          border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg)', color: 'var(--color-text)',
        }}
      >
        <option value="p">Paragraph</option>
        <option value="h2">Heading</option>
        <option value="h3">Subheading</option>
        <option value="h4">Minor heading</option>
      </select>
      <Btn title="Bold (Ctrl+B)" on={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={size} /></Btn>
      <Btn title="Italic (Ctrl+I)" on={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={size} /></Btn>
      <Btn title="Underline (Ctrl+U)" on={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={size} /></Btn>
      <Btn title="Strikethrough" on={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={size} /></Btn>
      <Sep />
      <Btn title="Bulleted list" on={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={size} /></Btn>
      <Btn title="Numbered list" on={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={size} /></Btn>
      <Btn title="Quote" on={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={size} /></Btn>
      <Btn title="Code block" on={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><Code2 size={size} /></Btn>
      <Sep />
      <Btn title="Link" on={editor.isActive('link') || panel === 'link'} onClick={() => setPanel(panel === 'link' ? null : 'link')}><Link2 size={size} /></Btn>
      <Btn
        title={canUpload ? 'Insert image (upload)' : 'Insert image by URL'}
        disabled={uploading}
        onClick={() => { if (canUpload) fileRef.current?.click(); else setPanel(panel === 'image' ? null : 'image') }}
      >
        {uploading ? <Loader2 size={size} className="tw-spin" /> : <ImageIcon size={size} />}
      </Btn>
      <Btn title="Insert table" on={editor.isActive('table')} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon size={size} /></Btn>
      <Btn title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={size} /></Btn>
      <Sep />
      <Btn title="Undo (Ctrl+Z)" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={size} /></Btn>
      <Btn title="Redo (Ctrl+Shift+Z)" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={size} /></Btn>
      {uploading && <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginLeft: 6 }}>Uploading image…</span>}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_IMAGES}
        multiple
        style={{ display: 'none' }}
        onChange={e => {
          const files = Array.from(e.target.files || [])
          e.target.value = ''
          if (files.length) onPickFiles(files)
        }}
      />
    </div>
  )
}

// ── Inline panels (no browser prompt() — it blocks the page) ─────────────

const barStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
  padding: '6px 8px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)', fontSize: '0.8425rem',
}
const inputStyle: React.CSSProperties = {
  flex: 1, minWidth: 160, height: 28, padding: '0 8px', fontSize: '0.8825rem',
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const smallBtn: React.CSSProperties = {
  height: 28, padding: '0 10px', fontSize: '0.8425rem', borderRadius: 4, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
}

function normaliseHref(raw: string): string | null {
  const v = raw.trim()
  if (!v) return null
  if (/^(https?:|mailto:|tel:|\/|#)/i.test(v)) return v
  if (/^[\w.-]+@[\w-]+\.[\w.]+$/.test(v)) return `mailto:${v}`
  if (/^[\w-]+(\.[\w-]+)+/.test(v)) return `https://${v}`
  return null
}

function LinkPanel({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const [href, setHref] = useState<string>(editor.getAttributes('link').href || '')
  const [newTab, setNewTab] = useState<boolean>(editor.getAttributes('link').target === '_blank')
  const [err, setErr] = useState('')
  const apply = () => {
    const url = normaliseHref(href)
    if (!url) { setErr('Enter a web address, email, or a path starting with /'); return }
    const chain = editor.chain().focus().extendMarkRange('link')
    chain.setLink({ href: url, target: newTab ? '_blank' : null } as { href: string; target?: string | null }).run()
    onClose()
  }
  return (
    <div style={barStyle}>
      <input
        autoFocus
        value={href}
        placeholder="https://example.com, /about, or name@example.com"
        onChange={e => { setHref(e.target.value); setErr('') }}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply() } if (e.key === 'Escape') onClose() }}
        style={inputStyle}
      />
      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input type="checkbox" checked={newTab} onChange={e => setNewTab(e.target.checked)} /> New tab
      </label>
      <button type="button" style={smallBtn} onClick={apply}>Apply</button>
      {editor.isActive('link') && (
        <button type="button" style={smallBtn} onClick={() => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); onClose() }}>Remove</button>
      )}
      <button type="button" style={{ ...smallBtn, border: 'none', background: 'transparent' }} onClick={onClose} aria-label="Close"><X size={14} /></button>
      {err && <span style={{ width: '100%', color: '#ef4444' }}>{err}</span>}
    </div>
  )
}

function ImageUrlPanel({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const [src, setSrc] = useState('')
  const [alt, setAlt] = useState('')
  const [err, setErr] = useState('')
  const apply = () => {
    const v = src.trim()
    if (!/^(https?:\/\/|\/)/i.test(v)) { setErr('Enter an image address starting with https:// or /'); return }
    editor.chain().focus().setImage({ src: v, alt: alt.trim() }).run()
    onClose()
  }
  return (
    <div style={barStyle}>
      <input autoFocus value={src} placeholder="Image address (https://…)" onChange={e => { setSrc(e.target.value); setErr('') }} style={inputStyle} />
      <input value={alt} placeholder="Describe the image (alt text)" onChange={e => setAlt(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply() } if (e.key === 'Escape') onClose() }}
        style={inputStyle} />
      <button type="button" style={smallBtn} onClick={apply}>Insert</button>
      <button type="button" style={{ ...smallBtn, border: 'none', background: 'transparent' }} onClick={onClose} aria-label="Close"><X size={14} /></button>
      {err && <span style={{ width: '100%', color: '#ef4444' }}>{err}</span>}
    </div>
  )
}

// Alt text matters for accessibility AND search; surface it whenever an image
// is selected rather than hiding it in a dialog.
function ImageAltBar({ editor }: { editor: Editor }) {
  const current = String(editor.getAttributes('image').alt || '')
  const [alt, setAlt] = useState(current)
  useEffect(() => { setAlt(current) }, [current])
  return (
    <div style={barStyle}>
      <span style={{ color: current ? 'var(--color-text-secondary)' : '#f59e0b', fontWeight: 600 }}>
        {current ? 'Alt text' : 'Add alt text'}
      </span>
      <input
        value={alt}
        placeholder="Describe the image for screen readers and search engines"
        onChange={e => setAlt(e.target.value)}
        onBlur={() => editor.chain().updateAttributes('image', { alt: alt.trim() }).run()}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); editor.chain().focus().updateAttributes('image', { alt: alt.trim() }).run() } }}
        style={inputStyle}
      />
      <button type="button" style={smallBtn} title="Remove image" onClick={() => editor.chain().focus().deleteSelection().run()}>
        <Trash2 size={13} />
      </button>
    </div>
  )
}

function TableBar({ editor }: { editor: Editor }) {
  const b = (label: string, fn: () => void) => (
    <button type="button" style={smallBtn} onMouseDown={e => e.preventDefault()} onClick={fn}>{label}</button>
  )
  const c = () => editor.chain().focus()
  return (
    <div style={barStyle}>
      <span style={{ color: 'var(--color-text-secondary)', fontWeight: 600 }}>Table</span>
      {b('+ Row', () => c().addRowAfter().run())}
      {b('+ Column', () => c().addColumnAfter().run())}
      {b('− Row', () => c().deleteRow().run())}
      {b('− Column', () => c().deleteColumn().run())}
      {b('Header row', () => c().toggleHeaderRow().run())}
      {b('Delete table', () => c().deleteTable().run())}
    </div>
  )
}

// Editor typography. Scoped to `.tw-richtext-editor` so it never leaks into
// the host; the published-page typography lives with the rich_content block.
const RICHTEXT_EDITOR_CSS = `
.tw-richtext-editor { outline: none; font-size: 1.0125rem; line-height: 1.6; color: var(--color-text); }
.tw-richtext-editor > * + * { margin-top: 0.6em; }
.tw-richtext-editor h2 { font-size: 1.4625rem; font-weight: 700; margin: 1em 0 0.3em; }
.tw-richtext-editor h3 { font-size: 1.2425rem; font-weight: 650; margin: 0.9em 0 0.3em; }
.tw-richtext-editor h4 { font-size: 1.0825rem; font-weight: 650; margin: 0.8em 0 0.2em; }
.tw-richtext-editor ul, .tw-richtext-editor ol { padding-left: 1.4em; }
.tw-richtext-editor blockquote { border-left: 3px solid var(--color-border); padding-left: 12px; color: var(--color-text-secondary); }
.tw-richtext-editor pre { background: var(--color-bg-secondary); padding: 10px 12px; border-radius: 6px; font-family: ui-monospace, Menlo, monospace; font-size: 0.9125rem; overflow-x: auto; }
.tw-richtext-editor code { font-family: ui-monospace, Menlo, monospace; font-size: 0.88em; }
.tw-richtext-editor a { color: var(--color-accent); text-decoration: underline; }
.tw-richtext-editor img { max-width: 100%; height: auto; border-radius: 4px; display: block; }
.tw-richtext-editor img.ProseMirror-selectednode { outline: 2px solid var(--color-accent); }
.tw-richtext-editor table { border-collapse: collapse; width: 100%; table-layout: fixed; }
.tw-richtext-editor th, .tw-richtext-editor td { border: 1px solid var(--color-border); padding: 6px 8px; vertical-align: top; }
.tw-richtext-editor th { background: var(--color-bg-secondary); font-weight: 600; text-align: left; }
.tw-richtext-editor .selectedCell { background: var(--color-accent-weak, rgba(76,141,255,0.12)); }
.tw-richtext-editor hr { border: none; border-top: 1px solid var(--color-border); margin: 1.2em 0; }
.tw-richtext-editor p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: var(--color-text-secondary); float: left; height: 0; pointer-events: none; }
.tw-spin { animation: tw-spin 1s linear infinite; }
@keyframes tw-spin { to { transform: rotate(360deg); } }
`

// ── Transaction-form binding ─────────────────────────────────────────────
// The form's RICHTEXT field: RichTextField wired to the host's runtime (image
// upload transport + notifier), so FormRenderer and the inline-grid dialog
// mount the same thing.

export function RichTextColumnField({
  project, objName, value, onChange, disabled, placeholder, hasError, fieldKey, minHeight, autoFocus,
}: {
  project: string
  objName: string
  value: unknown
  onChange: (html: string) => void
  disabled?: boolean
  placeholder?: string
  hasError?: boolean
  fieldKey?: string
  minHeight?: number
  autoFocus?: boolean
}) {
  const runtime = useTxnRuntime()
  const upload = runtime.uploadContentImage
  const uploadImage = useMemo(
    () => (upload ? (file: File) => upload(project, objName, file) : undefined),
    [upload, project, objName],
  )
  return (
    <RichTextField
      value={value == null ? '' : String(value)}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      uploadImage={uploadImage}
      onError={msg => runtime.notify.error(msg)}
      hasError={hasError}
      fieldKey={fieldKey}
      minHeight={minHeight}
      autoFocus={autoFocus}
    />
  )
}

// A RICHTEXT column inside an inline-editable GRID: a cell is no place for a
// toolbar, so it shows the text's first words and opens the full editor in a
// dialog. Changes apply to the row on "Done" (Cancel discards them).
export function RichTextCellEditor({
  project, objName, value, onChange, disabled, heading, hasError,
}: {
  project: string
  objName: string
  value: unknown
  onChange: (html: string) => void
  disabled?: boolean
  heading?: string
  hasError?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const text = richPlainText(value)
  return (
    <>
      <button
        type="button"
        onClick={() => { setDraft(value == null ? '' : String(value)); setOpen(true) }}
        title={text || (disabled ? '' : 'Click to write')}
        style={{
          width: '100%', minHeight: 28, textAlign: 'left', padding: '4px 8px', cursor: 'pointer',
          border: `1px solid ${hasError ? 'rgba(239,68,68,0.6)' : 'var(--color-border)'}`, borderRadius: 4,
          background: disabled ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
          color: text ? 'var(--color-text)' : 'var(--color-text-secondary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9125rem',
        }}
      >
        {text || (disabled ? '—' : 'Write…')}
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={heading || 'Edit text'}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div style={{
            width: 'min(880px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 10,
            background: 'var(--color-bg)', borderRadius: 10, padding: 16,
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', border: '1px solid var(--color-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: '1.0125rem' }}>{heading || 'Edit text'}</strong>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text)' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', minHeight: 0 }}>
              <RichTextColumnField
                project={project}
                objName={objName}
                value={draft}
                onChange={setDraft}
                disabled={disabled}
                minHeight={260}
                autoFocus
              />
            </div>
            {!disabled && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
                <button type="button" className="btn btn-primary btn-sm"
                  onClick={() => { onChange(draft); setOpen(false) }}>Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
